import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ChevronDown, Calendar, LayoutGrid, Map as MapIcon, Star } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { getCity } from "@/lib/city";
import { SearchBar } from "@/components/SearchBar";
import { SpotCard } from "@/components/SpotCard";
import { Rating } from "@/components/Rating";
import { ExplorerMap } from "@/components/ExplorerMap";
import { CATEGORY_ICONS } from "@/components/CategoryChip";
import { useLanguage } from "@/i18n/LanguageContext";

const HERO_IMAGE = "https://upload.wikimedia.org/wikipedia/commons/thumb/2/2a/Chateau_Frontenac_at_dusk_in_Quebec_City.jpg/1920px-Chateau_Frontenac_at_dusk_in_Quebec_City.jpg";

const ICONS = { ...CATEGORY_ICONS, LayoutGrid };

const FilterSelect = ({ label, icon: Icon, dot, value, options, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const current = options.find((o) => o.value === value);
  const isDefault = value === options[0].value;
  return (
    <div className="relative shrink-0 snap-start">
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
          !isDefault
            ? "border-blue-300 bg-[#EFF6FF] text-[#2563EB]"
            : "border-slate-200 bg-white text-slate-700 hover:border-blue-200"
        }`}
      >
        {dot && <span className="h-2 w-2 rounded-full bg-emerald-500" />}
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
        {isDefault ? t(label) : t(current?.label)}
        <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-50 mt-2 w-52 rounded-2xl border border-slate-100 bg-white p-1.5 shadow-xl">
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                data-testid={`${testId}-option-${o.value}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm transition hover:bg-blue-50 ${
                  value === o.value ? "font-semibold text-[#2563EB]" : "text-slate-700"
                }`}
              >
                {t(o.label)}
                {value === o.value && <Check className="h-4 w-4" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const RecommendedCard = ({ spot }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  return (
    <article
      data-testid={`recommended-card-${spot.slug}`}
      onClick={() => navigate(`/etablissement/${spot.slug}`)}
      className="flex min-w-[250px] shrink-0 cursor-pointer snap-start items-center gap-3 rounded-2xl border border-slate-100 bg-white p-2.5 shadow-[0_4px_16px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-[0_10px_24px_rgba(37,99,235,0.12)]"
    >
      <img src={spot.image} alt={spot.name} loading="lazy" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold text-slate-900">{spot.name}</h3>
        <p className="truncate text-xs text-slate-500">
          {t(spot.category_label)} • {spot.neighborhood}
        </p>
        <Rating value={spot.rating} count={spot.reviews_count} />
      </div>
    </article>
  );
};

export default function ExplorerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [spots, setSpots] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [cat, setCat] = useState(searchParams.get("categorie") || "tous");
  const [openNow, setOpenNow] = useState("tous");
  const [dist, setDist] = useState("toutes");
  const [note, setNote] = useState("toutes");
  const [quartier, setQuartier] = useState(searchParams.get("quartier") || "tous");
  const [jour, setJour] = useState("aujourd-hui");
  const [sort, setSort] = useState("proximite");
  const { t, lang } = useLanguage();
  const [view, setView] = useState(() => {
    if (searchParams.get("vue") === "carte") return "carte";
    return typeof window !== "undefined" && window.innerWidth < 1024 ? "liste" : "carte";
  });

  useEffect(() => {
    Promise.all([api.get("/establishments"), api.get("/categories"), api.get("/cities")])
      .then(([s, c, v]) => {
        setSpots(s.data);
        setCategories(c.data);
        setCities(v.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = {};
    if (query) params.q = query;
    if (cat !== "tous") params.categorie = cat;
    if (quartier !== "tous") params.quartier = quartier;
    if (view === "carte") params.vue = "carte";
    setSearchParams(params, { replace: true });
  }, [query, cat, quartier, view, setSearchParams]);

  const neighborhoods = useMemo(
    () => [...new Set(spots.map((s) => s.neighborhood))].sort(),
    [spots]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = spots.filter(
      (s) =>
        (cat === "tous" || s.category_key === cat) &&
        (!q ||
          s.name.toLowerCase().includes(q) ||
          s.category_label.toLowerCase().includes(q) ||
          s.neighborhood.toLowerCase().includes(q)) &&
        (openNow === "tous" || s.is_open) &&
        (dist === "toutes" || s.distance_m <= Number(dist)) &&
        (note === "toutes" || s.rating >= Number(note)) &&
        (quartier === "tous" || s.neighborhood === quartier)
    );
    return list.sort((a, b) => {
      if (sort === "note") return b.rating - a.rating;
      if (sort === "nom") return a.name.localeCompare(b.name, "fr");
      return a.distance_m - b.distance_m;
    });
  }, [spots, cat, query, openNow, dist, note, quartier, sort]);

  const recommended = useMemo(
    () => [...spots].sort((a, b) => b.rating - a.rating).slice(0, 4),
    [spots]
  );

  const cityName = getCity().split(",")[0];
  const allCats = [{ key: "tous", label: "Tous", icon: "LayoutGrid", color: "#2563EB" }, ...categories];

  return (
    <main data-testid="explorer-page" className="pb-16">
      <Seo
        title={t("Explorer — GoSpot")}
        siteName="GoSpot"
        description={t("Explore les meilleurs bars, karaokés, restaurants, activités et hébergements à Québec.")}
      />

      {/* BANDEAU TITRE + RECHERCHE */}
      <section className="relative overflow-hidden bg-[#EDF3FE]">
        <img
          src={HERO_IMAGE}
          alt={t("Château Frontenac à Québec")}
          className="absolute right-0 top-0 hidden h-full w-1/2 object-cover md:block"
        />
        <div className="absolute inset-0 hidden bg-gradient-to-r from-[#EDF3FE] via-[#EDF3FE]/70 to-transparent md:block" />
        <div className="relative mx-auto max-w-[1800px] px-4 pb-10 pt-10 sm:px-6 sm:pt-14">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
          >
            {t("Explorer les meilleurs spots")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-3 max-w-xl text-base text-slate-600"
          >
            {t("Trouve bars, karaokés, restaurants, activités et hébergements près de toi.")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.22 }}
            className="mt-6 max-w-2xl"
          >
            <SearchBar cities={cities} initialQuery={query} onSearch={(q) => setQuery(q)} />
          </motion.div>
        </div>
      </section>

      {/* CATÉGORIES */}
      <section className="mx-auto mt-6 max-w-[1800px] px-4 sm:px-6 lg:px-10">
        <div className="no-scrollbar flex snap-x gap-2.5 overflow-x-auto pb-1">
          {allCats.map((c) => {
            const Icon = ICONS[c.icon] || LayoutGrid;
            const active = cat === c.key;
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`explorer-chip-${c.key}`}
                onClick={() => setCat(c.key)}
                className={`flex shrink-0 snap-start items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition duration-300 ${
                  active
                    ? "border-[#2563EB] bg-[#2563EB] text-white shadow-[0_6px_18px_rgba(37,99,235,0.25)]"
                    : "border-slate-200 bg-white text-slate-700 hover:-translate-y-0.5 hover:border-blue-200"
                }`}
              >
                <Icon className="h-4 w-4" style={{ color: active ? "#fff" : c.color }} />
                {t(c.label)}
              </button>
            );
          })}
        </div>

        {/* FILTRES */}
        <div className="no-scrollbar mt-4 flex snap-x gap-2.5 overflow-x-auto pb-1">
          <FilterSelect
            label="Ouvert maintenant"
            dot
            value={openNow}
            onChange={setOpenNow}
            testId="filter-ouvert"
            options={[
              { value: "tous", label: "Tous les horaires" },
              { value: "ouvert", label: "Ouvert maintenant" },
            ]}
          />
          <FilterSelect
            label="Distance"
            value={dist}
            onChange={setDist}
            testId="filter-distance"
            options={[
              { value: "toutes", label: "Toutes distances" },
              { value: "500", label: "Moins de 500 m" },
              { value: "1000", label: "Moins de 1 km" },
              { value: "2000", label: "Moins de 2 km" },
            ]}
          />
          <FilterSelect
            label="Note"
            value={note}
            onChange={setNote}
            testId="filter-note"
            options={[
              { value: "toutes", label: "Toutes les notes" },
              { value: "4", label: "4,0 et plus" },
              { value: "4.5", label: "4,5 et plus" },
            ]}
          />
          <FilterSelect
            label="Quartier"
            value={quartier}
            onChange={setQuartier}
            testId="filter-quartier"
            options={[
              { value: "tous", label: "Tous les quartiers" },
              ...neighborhoods.map((n) => ({ value: n, label: n })),
            ]}
          />
          <FilterSelect
            label="Aujourd’hui"
            icon={Calendar}
            value={jour}
            onChange={setJour}
            testId="filter-jour"
            options={[
              { value: "aujourd-hui", label: "Aujourd’hui" },
              { value: "demain", label: "Demain" },
              { value: "ce-week-end", label: "Ce week-end" },
            ]}
          />
        </div>

        {/* RÉSULTATS + TRI */}
        <div className="mt-6 flex items-center justify-between gap-4">
          <p data-testid="results-count" className="text-sm font-bold text-slate-900">
            {lang === "en"
              ? `${filtered.length} result${filtered.length > 1 ? "s" : ""} in ${cityName}`
              : `${filtered.length} résultat${filtered.length > 1 ? "s" : ""} à ${cityName}`}
          </p>
          <label className="flex shrink-0 items-center gap-1.5 text-sm text-slate-500">
            <span className="hidden sm:inline">{t("Trier par :")}</span>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="cursor-pointer bg-transparent font-semibold text-[#2563EB] outline-none"
            >
              <option value="proximite">{t("proximité")}</option>
              <option value="note">{t("note")}</option>
              <option value="nom">{t("nom")}</option>
            </select>
          </label>
        </div>

        {/* GRILLE + CARTE */}
        <div className="mt-4 flex items-start gap-6">
          <div
            className={
              view === "carte"
                ? "hidden flex-1 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid xl:grid-cols-3"
                : "grid flex-1 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            }
          >
            {loading ? (
              [0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-64 animate-pulse rounded-3xl bg-white" />
              ))
            ) : filtered.length === 0 ? (
              <div data-testid="no-results" className="col-span-full rounded-3xl border border-slate-100 bg-white p-10 text-center">
                <p className="font-semibold text-slate-900">{t("Aucun résultat")}</p>
                <p className="mt-1 text-sm text-slate-500">{t("Essaie d’ajuster ta recherche ou tes filtres.")}</p>
                <button
                  type="button"
                  data-testid="reset-filters-button"
                  onClick={() => {
                    setQuery("");
                    setCat("tous");
                    setOpenNow("tous");
                    setDist("toutes");
                    setNote("toutes");
                    setQuartier("tous");
                  }}
                  className="mt-4 rounded-full bg-[#2563EB] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8]"
                >
                  {t("Réinitialiser les filtres")}
                </button>
              </div>
            ) : (
              filtered.map((spot) => <SpotCard key={spot.slug} spot={spot} />)
            )}
          </div>

          {view === "carte" && (
            <aside className="w-full shrink-0 lg:sticky lg:top-24 lg:w-[400px] xl:w-[440px]">
              <ExplorerMap markers={filtered} view={view} onViewChange={setView} />
            </aside>
          )}
        </div>

        {view === "liste" && (
          <button
            type="button"
            data-testid="show-map-fab"
            onClick={() => setView("carte")}
            className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white shadow-xl transition hover:bg-[#1D4ED8]"
          >
            <MapIcon className="h-4 w-4" />
            {t("Voir la carte")}
          </button>
        )}
      </section>

      {/* RECOMMANDÉ POUR TOI */}
      <section className="mx-auto mt-14 max-w-[1800px] px-4 sm:px-6 lg:px-10">
        <div
          data-testid="recommended-section"
          className="flex flex-col gap-5 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:p-6"
        >
          <div className="flex shrink-0 items-center gap-3 sm:w-60 sm:flex-col sm:items-start">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#2563EB]">
              <Star className="h-6 w-6 fill-white text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">{t("Recommandé pour toi")}</h2>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                {lang === "en" ? `Based on your favourites and searches in ${cityName}` : `Basé sur tes favoris et tes recherches à ${cityName}`}
              </p>
            </div>
          </div>
          <div className="no-scrollbar flex snap-x gap-3 overflow-x-auto pb-1">
            {recommended.map((spot) => (
              <RecommendedCard key={spot.slug} spot={spot} />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
